import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import AddNewsModal from "components/Home/AddNewsModal";
import { MoreVertical } from "lucide-react";
import "./AdminLatestNews.css";
import { highlightText } from "components/utils/SearchHighlight";
import styled from "styled-components";
import { formatDate } from "components/utils/DateUtils";
import openNotification from "./utils/CustomNotification";
import AdminConfigLoader from "components/Loaders/AdminConfigLoader";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";
import { SessionExtend } from "components/utils/SessionCheckUtils";
import { withUIDirectives } from "components/utils/UI_Directive/showUIDirectives";
import { FaHistory } from "react-icons/fa";
import HistoryModal from "./utils/HistoryModal";

function AdminLatestNews() {
  useCheckSessionHeartBeat();
  const menuRef = useRef<HTMLDivElement>(null);
  const beAddress =
    window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";
  const [newsItems, setNewsItems] = useState<any[]>([]);
  const [filteredItems, setFilteredItems] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [menuOpenIndex, setMenuOpenIndex] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newsToDelete, setNewsToDelete] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<"Active" | "upcoming" | "expired">(
    "Active"
  );
  const [isLoading, setIsLoading] = useState(true);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyUid, setHistoryUid] = useState<string | null>(null);
  const [carousalHistoryMap, setCarousalHistoryMap] = useState<
    Record<string, string | null>
  >({});

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpenIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const now = new Date();
    const filtered = newsItems.filter((item) => {
      const start = new Date(item.from_datetime);
      const end = new Date(item.to_datetime);
      if (activeTab === "Active") return start <= now && now <= end;
      if (activeTab === "upcoming") return start > now;
      if (activeTab === "expired") return end < now;
      return true;
    });
    const searchLower = search.toLowerCase();
    setFilteredItems(
      filtered.filter(
        (item) =>
          item.title?.toLowerCase().includes(searchLower) ||
          item.description?.toLowerCase().includes(searchLower)
      )
    );
  }, [activeTab, newsItems, search]);

  const formatUtcAsLocalText = (isoString: string) => {
    if (!isoString) return "";
    const [datePart, timePart] = isoString.split("T"); // 2025-07-22 + 15:00:00+00:00
    const [hour, minute] = timePart.split(":");

    const dateObj = new Date(datePart); // local date
    const month = dateObj.toLocaleString("en-US", { month: "short" });
    const day = dateObj.getDate();

    // Convert 15 → 3 PM
    let hr = parseInt(hour, 10);
    const ampm = hr >= 12 ? "PM" : "AM";
    hr = hr % 12 || 12;

    return `${month} ${day}, ${hr}:${minute} ${ampm}`;
  };

  const fetchNews = useCallback(async () => {
    setIsLoading(true);
    try {
      const start = new Date();
      start.setDate(1);
      start.setHours(0, 0, 0, 0);

      const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);

      const response = await axios.get(`${beAddress}/api/carousal`, {
        headers: {
          "user-uid": userUid,
        }, withCredentials: true,
        params: {
          from_datetime: start.toISOString(),
          to_datetime: end.toISOString(),
        },
      });

      const result = withUIDirectives(response.data);
      const newsData = result?.data || [];

      if (!newsData.length) {
        setNewsItems([]);
        setFilteredItems([]);
        setCarousalHistoryMap({});
        setIsLoading(false);
        return;
      }

      const imageUids = newsData
        .map((item: any) => item.image_uid)
        .filter(Boolean);

      if (!imageUids.length) {
        setNewsItems(newsData);
        setFilteredItems(newsData);
        setIsLoading(false);
        return;
      }

      let images: any[] = [];
      try {
        const imagesResponse = await axios.get(
          `${beAddress}/api/get-image-by-uid`,
          {
            headers: {
              "user-uid": userUid,
            }, withCredentials: true,
            params: {
              image_uid_list: imageUids,
              is_icon_img: false,
            },
            paramsSerializer: (params) =>
              Object.keys(params)
                .map((key) =>
                  Array.isArray(params[key])
                    ? params[key]
                      .map(
                        (val: string) => `${key}=${encodeURIComponent(val)}`
                      )
                      .join("&")
                    : `${key}=${encodeURIComponent(params[key])}`
                )
                .join("&"),
          }
        );

        images = imagesResponse.data?.data || [];
      } catch (imageErr) {
        console.warn("Image fetch failed or skipped", imageErr);
      }

      const finalNews = newsData.map((news: any) => ({
        ...news,
        image:
          images.find((img: any) => img.image_uid === news.image_uid)?.image ||
          "",
      }));

      const carousalMap: Record<string, string | null> = {};
      finalNews.forEach((item: any) => {
        carousalMap[item.carousal_uid] = item.history_uid || null;
      });
      setCarousalHistoryMap(carousalMap);

      setNewsItems(finalNews);
      setFilteredItems(finalNews);
      SessionExtend();
    } catch (err) {
      openNotification({
        type: "error",
        message: "Error Fetching Events",
      });
    } finally {
      setIsLoading(false);
    }
  }, [beAddress, sessionUid, userUid]);

  // After your fetchNews useEffect or fetch function
  // useEffect(() => {
  //   if (newsItems && newsItems.length > 0) {
  //     const firstHistoryItem = newsItems.find((item: any) => item.history_uid);
  //     if (firstHistoryItem && firstHistoryItem.history_uid) {
  //       setHistoryUid(firstHistoryItem.history_uid);
  //     }
  //   }
  // }, [newsItems]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const handleSearch = (value: string) => {
    setSearch(value);
    setFilteredItems(
      newsItems.filter(
        (item) =>
          item.title?.toLowerCase().includes(value.toLowerCase()) ||
          item.description?.toLowerCase().includes(value.toLowerCase())
      )
    );
  };

  //   const handleDelete = async (item: any) => {
  //     if (window.confirm('Are you sure you want to delete this news?')) {
  //       try {
  //         await axios.delete(`${beAddress}/api/delete-news`, {
  //           headers: {
  //             'session-uid': sessionUid,
  //             'user-uid': userUid,
  //           },
  //           data: { uid: item.uid },
  //         });
  //         fetchNews();
  //       } catch (err) {
  //         console.error('Failed to delete news', err);
  //       }
  //     }
  //   };

  return (
    <div className="admin-news-wrapper">
      <div className="LatestNewsHeadingSection">Latest News Configuration</div>

      <div className="admin-news-header">
        <input
          type="text"
          className="news-search-input"
          placeholder="Search news..."
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <button
          className="add-news-button"
          onClick={() => setShowAddModal(true)}
        >
          + Add News / Event
        </button>
      </div>

      <NewsTabContainer>
        <button
          onClick={() => setActiveTab("upcoming")}
          className={activeTab === "upcoming" ? "active" : ""}
        >
          Upcoming
        </button>
        <button
          onClick={() => setActiveTab("Active")}
          className={activeTab === "Active" ? "active" : ""}
        >
          Ongoing
        </button>
        <button
          onClick={() => setActiveTab("expired")}
          className={activeTab === "expired" ? "active" : ""}
        >
          Expired
        </button>
      </NewsTabContainer>

      {isLoading ? (
        <AdminConfigLoader />
      ) : (
        <div
          className={
            filteredItems.length === 0
              ? "news-empty-container"
              : "news-cards-container"
          }
        >
          {filteredItems.length === 0 ? (
            <div className="no-news-text">
              No news or events to show for this period.
            </div>
          ) : (
            filteredItems.map((item, index) => {
              const isUpcoming = activeTab === "upcoming";

              return (
                <div key={index} className="news-card">
                  <img
                    src={`data:image/jpeg;base64,${item.image}`}
                    alt="News"
                    className="news-card-img"
                  />

                  <div className="news-card-content">
                    <div
                      className="news-card-left"
                      style={{ position: "relative" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <h3>{highlightText(item.title, search)}</h3>
                        <FaHistory
                          className="latest-news-view-history-btn"
                          onClick={() => {
                            // Use the mapping
                            const mappedHistoryUid =
                              carousalHistoryMap[item.carousal_uid] || null;
                            if (mappedHistoryUid) {
                              setHistoryUid(mappedHistoryUid);
                            } else {
                              setHistoryUid(null); // reset if no history
                            }
                            setShowHistoryModal(true);
                          }}
                          style={{
                            fontSize: "15px",
                            color: "black",
                            opacity: item.history_uid ? 1 : 0.5,
                          }}
                          title="View History"
                        />
                      </div>

                      <p>{highlightText(item.description, search)}</p>
                      <div className="news-card-date-line">
                        <strong>
                          {formatUtcAsLocalText(item.from_datetime)}
                        </strong>{" "}
                        to{" "}
                        <strong>
                          {formatUtcAsLocalText(item.to_datetime)}
                        </strong>
                      </div>
                    </div>

                    {isUpcoming && (
                      <div
                        className="news-card-menu-icon"
                        onClick={() =>
                          setMenuOpenIndex(
                            menuOpenIndex === index ? null : index
                          )
                        }
                      >
                        <MoreVertical size={18} />
                      </div>
                    )}
                  </div>

                  {isUpcoming && menuOpenIndex === index && (
                    <div ref={menuRef} className="news-card-context-menu">
                      <button
                        onClick={() => {
                          setEditData(item);
                          setShowEditModal(true);
                          setMenuOpenIndex(null);
                        }}
                      >
                        Edit news / event
                      </button>
                      <button
                        onClick={() => {
                          setNewsToDelete(item);
                          setShowDeleteModal(true);
                          setMenuOpenIndex(null);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

      )}
      {showAddModal && (
        <AddNewsModal
          onClose={() => setShowAddModal(false)}
          onSuccess={fetchNews}
        />
      )}

      {showEditModal && editData && (
        <AddNewsModal
          onClose={() => setShowEditModal(false)}
          onSuccess={fetchNews}
          initialData={editData}
          isEdit={true}
        />
      )}

      {showDeleteModal && newsToDelete && (
        <div className="AdminLatestNews-delete-modal-overlay">
          <div className="AdminLatestNews-delete-modal">
            <p>Are you sure you want to delete this news item?</p>
            <div className="AdminLatestNews-delete-modal-buttons">
              <button
                className="AdminLatestNews-confirm"
                onClick={async () => {
                  try {
                    const response = await axios.delete(
                      `${beAddress}/api/carousal`,
                      {
                        headers: {
                          "user-uid": userUid,
                        }, withCredentials: true,
                        params: {
                          carousal_uid: newsToDelete.carousal_uid,
                        },
                      }
                    );

                    withUIDirectives(response.data);

                    openNotification({
                      type: "success",
                      message: "Event Deleted Sucessfully",
                    });

                    setShowDeleteModal(false);
                    setNewsToDelete(null);
                    fetchNews();
                    SessionExtend();
                  } catch (err) {
                    openNotification({
                      type: "error",
                      message: "Failed to Delete Event",
                    });
                  }
                }}
              >
                Yes, Delete
              </button>

              <button
                className="AdminLatestNews-cancel"
                onClick={() => {
                  setShowDeleteModal(false);
                  setNewsToDelete(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <HistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        params={{ history_uid: historyUid! }}
      />
    </div>
  );
}

export default AdminLatestNews;
const NewsTabContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap; 
  gap: 12px;
  width: 100%;
  margin: 16px 0;

  button {
    border: 1px solid grey;
    padding: 10px 20px;
    font-weight: 600;
    border-radius: 5px;
    color: #333;
    cursor: pointer;
    background-color: white;
    transition: all 0.3s ease-in-out;
    min-width: 120px;
  }

  .active {
    background-color: var(--sidebar-hover-color);
    color: var(--text-color);
  }

 
  @media (max-width: 1024px) {
    gap: 10px;
    button {
      padding: 8px 18px;
      font-size: 0.9rem;
    }
  }

 
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 8px;

    button {
      width: 100%;
      max-width: 300px;
      font-size: 0.9rem;
      padding: 8px 12px;
    }
  }
`;


